import { UploadFile } from 'antd/lib/upload/interface';
import { AxiosRequestConfig } from 'axios';
import { FileAttachment } from 'api/attachments';
import { AttachmentType } from 'api/attachments/types';
import { HttpClient } from 'api/http-client';
import { ProjectId } from 'api/models';
import { UpdateFolderFormProps } from 'components/organisms/projects/files/update-folder-modal';
import { ProjectFileGroup, ProjectTaskAttachments } from './types';

type CreateFileGroupPayload = Pick<
  ProjectFileGroup,
  'folderName' | 'isPrivate'
>;

export type RearrangeFilesPayload = {
  attachmentId: number;
  newPosition: number;
  folderId: number | null;
};

export type RearrangeFoldersPayload = {
  folderId: number;
  newPosition: number;
};

type UploadFileResponse = {
  attachment: FileAttachment;
};

class ProjectFilesHttpClient extends HttpClient {
  public async createFileGroup(
    [, projectId]: ProjectId,
    payload: CreateFileGroupPayload,
  ): Promise<ProjectFileGroup> {
    return this.post(`/projects/${projectId}/folders`, payload);
  }

  public async updateFolder(
    [, projectId]: ProjectId,
    folderId: number,
    payload: UpdateFolderFormProps,
  ): Promise<ProjectFileGroup> {
    return this.put(`/projects/${projectId}/folders/${folderId}`, payload);
  }

  public getFileGroups([, projectId]: ProjectId): Promise<ProjectFileGroup[]> {
    return this.get(`/projects/${projectId}/folders`);
  }

  public getTaskAttachments([, projectId]: ProjectId): Promise<
    ProjectTaskAttachments[]
  > {
    return this.get(`/projects/${projectId}/task-attachments`);
  }

  public getAttachments([, projectId]: ProjectId): Promise<FileAttachment[]> {
    return this.get(`/attachments/project/${projectId}`);
  }

  public getFileDrive([, projectId]: ProjectId): Promise<{
    folders: ProjectFileGroup[];
    tasks: ProjectTaskAttachments[];
  }> {
    return this.get(`/projects/${projectId}/drive`);
  }

  public async uploadFile(
    file: UploadFile,
    data: { projectId: number; folderId: number },
    config: AxiosRequestConfig,
  ): Promise<FileAttachment> {
    if (!file.originFileObj) {
      return Promise.reject(new Error('file object is empty'));
    }
    const formData = new FormData();
    const fileMeta = {
      attachment: {
        name: file.name,
        publicVisibility: true,
        projectId: data.projectId,
      },
      associatedEntity: {
        sourceType: AttachmentType.Folder,
        sourceId: data.folderId,
      },
    };
    const blob = new Blob([JSON.stringify(fileMeta)], {
      type: 'application/json',
    });

    formData.append('file', file.originFileObj);
    formData.append('request', blob);

    const res: UploadFileResponse = await this.post(
      '/attachments',
      formData,
      config,
    );

    return res.attachment;
  }

  public async linkFilesToProjects(
    [, projectId]: ProjectId,
    folderId: number,
    fileIds: number[],
    config?: AxiosRequestConfig,
  ): Promise<UploadFile> {
    return this.post(
      `/projects/${projectId}/folders/${folderId}/attachments/link`,
      fileIds,
      config,
    );
  }

  public getFileDownloadUrl(
    [, projectId]: ProjectId,
    folderId: number,
    fileId: number,
  ): string {
    return `${this.BASEURL}/projects/${projectId}/folders/${folderId}/attachments/${fileId}/download`;
  }

  public getFileGroupDownloadUrl(
    [, projectId]: ProjectId,
    folderId: number,
  ): string {
    return `${this.BASEURL}/projects/${projectId}/folders/${folderId}/download`;
  }

  public triggerEmailForFileGroup([, projectId]: ProjectId, folderId: number) {
    return this.get(
      `${this.BASEURL}/projects/${projectId}/folders/${folderId}/email`,
    );
  }

  public async deleteFile(
    [, projectId]: ProjectId,
    folderId: number,
    fileId: number,
  ): Promise<FileAttachment> {
    return this.delete(
      `/projects/${projectId}/folders/${folderId}/attachments`,
      {
        data: [fileId],
      },
    );
  }

  public async rearrangeFiles(
    [, projectId]: ProjectId,
    payload: RearrangeFilesPayload,
  ): Promise<void> {
    return this.put(
      `/projects/${projectId}/folders/reorder-attachments`,
      payload,
    );
  }

  public async rearrangeFolders(
    [, projectId]: ProjectId,
    payload: RearrangeFoldersPayload,
  ): Promise<void> {
    return this.put(`/projects/${projectId}/reorder-folder`, payload);
  }

  public async deleteFileGroup(
    [, projectId]: ProjectId,
    folderId: number,
  ): Promise<ProjectFileGroup> {
    return this.delete(`/projects/${projectId}/folders/${folderId}`);
  }

  public async getDownloadToken(emailId: string): Promise<string> {
    return this.get(
      `/token/generate?emailId=${emailId}&linkType=FORGOT_PASSWORD`,
    );
  }
}

export const ProjectFilesAPI = new ProjectFilesHttpClient();
